import AuthCli from './AuthCli'

/**
 * JavaScript version of the Outbox service API.
 */
export default class OutboxCli {

  //
  // Path constants:
  //
  static get SERVICE_ROUTE() { return 'outbox' }
  static get DRAFT_ID_PATH() { return 'draftid' }
  static get BILLABLE_DRAFT_PATH() { return 'billabledrafts' }
  static get DRAFT_PATH() { return 'drafts' }
  static get PAGE_SIZE_QUERY() { return 'pagesize' }
  static get PAGE_QUERY() { return 'page' }
  static get INCLUDE_USED_QUERY() { return 'includeused' }
  static get DRAFT_PAGE_THUMB_PATH() { return 'draftpagethumb' }
  static get PAGE_NUMBER_QUERY() { return 'pagenumber' }
  static get DRAFT_PAGE_IMAGE_PATH() { return 'draftpageimage' }
  static get LEFT_QUERY() { return 'left' }
  static get TOP_QUERY() { return 'top' }
  static get WIDTH_QUERY() { return 'width' }
  static get HEIGHT_QUERY() { return 'height' }
  static get SHIPMENT_PATH() { return 'shipments' }
  static get SHIPMENTS_BATCH_PATH() { return 'shipmentsbatch' }
  static get BILLABLE_SHIPMENT_PATH() { return 'billableshipments' }
  static get SHIPMENT_CODE_PATH() { return 'shipmentcode' }
  static get CANCELLATION_REASON_QUERY() { return 'cancellationReason' }
  static get SHIPMENT_LIST_PATH() { return 'shipmentlist' }
  static get SHIPMENT_CODES_QUERY() { return 'shipmentcodes' }
  static get SEND_CONFIRMATION_QUERY() { return 'sendconfirmation' }
  static get REMOVE_DRAFT_QUERY() { return 'removedraft' }
  static get SHIPMENT_ID_PATH() { return 'shipmentid' }
  static get DO_NOT_CANCEL_QUERY() { return 'donotcancel' }
  static get SHIPMENTID_OR_CODE_PATH() { return 'shipmentidorcode' }
  static get SHIPMENT_PAGE_IMAGE_PATH() { return 'shipmentpageimage' }
  static get THUMB_QUERY() { return 'thumbnail' }
  static get FILE_CONTENT_PATH() { return 'filecontent' }
  static get DETECTED_ADDRESSES() { return 'detectedaddresses' }
  static get DETECTED_REFERENCES() { return 'detectedreferences' }
  static get DETECTED_SHIPMENTS() { return 'detectedshipments' }
  static get DRAFT_IDS_QUERY() { return 'draftids' }
  static get DRAFT_PAGE_SAMPLE_PATH() { return 'draftpagesample' }
  static get BILLABLE_ID_PATH() { return 'billableid' }
  static get SAMPLE_NUMBER_QUERY() { return 'samplenumber' }
  static get INCLUDE_EMITTED() { return 'includeemitted' }
  static get INCLUDE_PROGRAMMED() { return 'includeprogrammed' }
  static get INCLUDE_SAVED() { return 'includesaved' }
  static get FILTER_QUERY() { return 'filter' }
  static get SHIPMENT_COUNT_PATH() { return 'shipmentscount' }
  static get BILLABLE_SHIPMENT_COUNT_PATH() { return 'billableshipmentscount' }
  static get BILLABLE_SHIPMENT_BILLING_TOTALS_PATH() { return 'billableshipmentsbillingtotals' }
  static get BILLABLE_FILE_CONTENT_PATH() { return 'billablefilecontent' }
  static get REDISPATCHED_PATH() { return 'redispatched' }
  static get SHIPMENT_SEARCH_PATH() { return 'shipmentsearch' }  
  static get BILLABLE_ID_FILTER_QUERY() { return 'billableidfilter' }
  static get USER_ID_QUERY() { return 'userId' }
  static get SEARCH_TEXT_QUERY() { return 'searchText' }
  static get BATCH_BY_USER_PATH() { return 'batchesbyuser' }
  static get BATCH_OF_BILLABLE_PATH() { return 'billablebatches' }
  

  // URL prefix for debugging environment vs behind reverse proxy environment.
  static get devPrefix() { return (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') ? 'http://localhost:6008/' : window.location.protocol + '//' + window.location.host + '/api/' }

  /**
   * Provides the information of an existing draft given its identifier.
   * The calling user must be the owner of the draft.
   * @param {string} draftId Internal unique identifier of the draft asking for
   * @returns {DraftInfo} Information about the draft, error if not found
   */
  static async GetDraft(draftId) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + draftId
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides the information of an existing draft given its identifier.
   * The calling user must the administrator of the billable the draft belongs to.
   * @param {string} draftId Internal unique identifier of the draft asking for
   * @returns {DraftInfo} Information about the draft, error if not found
   */
  static async GetDraftOfBillable(draftId) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.BILLABLE_DRAFT_PATH + '/' + draftId
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides the list of draft documents currently uploaded by the calling user in the current billable.
   * This is a paging request, the response may not contain all drafts.
   * For paging considerations, the collection of drafts of the calling user is provided sorted by upload time descending.
   * @param {int} pageSize Paging argument: maximum amount of drafts to be provided in this call
   * @param {int} page Paging argument: the page to provide, 0-based
   * @param {boolean} includeUsed Whether to provide draft documents already used for shipments, otherwise only unused drafts will be provided
   * @returns {DraftInfo[]} (string draftId, string documentName, DateTime uploadTime, string ownerUser, int timesUsed)
   */
  static async GetDrafts(pageSize, page, includeUsed) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.DRAFT_PATH + "?" + this.PAGE_SIZE_QUERY + "=" + pageSize + "&" + this.PAGE_QUERY + "=" + page + "&" + this.INCLUDE_USED_QUERY + "=" + includeUsed
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides the list of draft documents currently uploaded by any user in the current billable.
   * This is to be used by working group administrators which are allowed to access content from any user in the billable.
   * This is a paging request, the response may not contain all drafts.
   * For paging considerations, the collection of drafts of the calling user is provided sorted by upload time descending.
   * @param {int} pageSize Paging argument: maximum amount of drafts to be provided in this call
   * @param {int} page Paging argument: the page to provide, 0-based
   * @param {boolean} includeUsed Whether to provide draft documents already used for shipments, otherwise only unused drafts will be provided
   * @returns {DraftInfo[]} (string draftId, string documentName, DateTime uploadTime, string ownerUser, int timesUsed)
   */
  static async GetDraftsOfBillable(pageSize, page, includeUsed) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.BILLABLE_DRAFT_PATH + "?" + this.PAGE_SIZE_QUERY + "=" + pageSize + "&" + this.PAGE_QUERY + "=" + page + "&" + this.INCLUDE_USED_QUERY + "=" + includeUsed
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides the base-64 thumbnail image of one of the specified draft document's page.
   * @param {string} draftId Internal unique identifier of the draft asking for
   * @param {int} pageNumber Number of the page in the draft document (starting from page 1)
   * @returns {string} Low resolution image of the page's content in base-64 format
   */
  static async GetDraftPageThumb(draftId, pageNumber) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.DRAFT_PAGE_THUMB_PATH + '/' + draftId + "?" + this.PAGE_NUMBER_QUERY + "=" + pageNumber
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides the base-64 image of one of the specified draft document's page.
   * Allows to specify a rectangle of the page to be provided instead of the whole page's image.
   * Hint: passing the following values will provide the whole page: left=0, top=0, width=100, height=100.
   * @param {string} draftId Internal unique identifier of the draft asking for
   * @param {int} pageNumber Number of the page in the draft document (starting from page 1)
   * @param {double} left Percentage of the whole image's width where the rectangle to provide will begin in its left edge
   * @param {double} top Percentage of the whole image's height where the rectangle to provide will begin in its top edge
   * @param {double} width Percentage of the whole image's width the provided rectangle will have
   * @param {double} height Percentage of the whole image's height the provided rectangle will have
   * @returns {string} High resolution image of the page's content in base-64 format
   */
  static async GetDraftPageImage(draftId, pageNumber, left, top, width, height) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.DRAFT_PAGE_IMAGE_PATH + '/' + draftId +
      "?" + this.PAGE_NUMBER_QUERY + "=" + pageNumber +
      "&" + this.LEFT_QUERY + "=" + left +
      "&" + this.TOP_QUERY + "=" + top +
      "&" + this.WIDTH_QUERY + "=" + width +
      "&" + this.HEIGHT_QUERY + "=" + height
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides the base-64 image of one page of a draft. The page will be the first page of a draft belonging
   * to the specified billable (the specific page to provide is selected by the service).
   * Used to display any draft's first page for OCR area selection in the web application.
   * @param {*} billableId Internal identifier of the billable asking for one of its drafts (of any of its users)
   * @param {*} sampleNumber 0-based index to select one of the available drafts. Usually to pass 0, but the user may prefer to try with others (if the first option is not proper for OCR area selection)
   * @returns Medium resolution image of the pag's content in base-64 format
   */
  static async GetDraftPageSample(billableId, sampleNumber) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.DRAFT_PAGE_SAMPLE_PATH + '/' + billableId +
      "?" + this.SAMPLE_NUMBER_QUERY + "=" + sampleNumber
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Adds new draft documents into the collection of drafts for the calling user in the current billable.
   * Note that this draft will be bound to the billable of the current session and so owned by the session's user accessible only
   * when the calling session is bound to this same billable.
   * @param {multi-part formData} body 
   * @param {string} contentType 
   * @returns 
   */
  static async UploadDrafts(body) {
    var verb = 'POST'
    var path = this.SERVICE_ROUTE + '/' + this.DRAFT_PATH

    return AuthCli.Fetch(this.devPrefix + path, verb, body, true)
  }

  /**
   * Allow changing the draft document's name from the original name given when uploaded.
   * This method is valid for drafts owned by the calling user.
   * @param {DraftInfo} draft Information about the draft including the desired new name. Other fields are ignored
   */
  static async RenameDraft(draft) {
    var verb = 'PUT'
    var path = this.SERVICE_ROUTE + '/' + this.DRAFT_PATH
    var body = draft

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }
  
  /**
   * Allow changing the draft document's name from the original name given when uploaded.
   * This method is valid for drafts in the billable of the calling administrator.
   * @param {DraftInfo} draft Information about the draft including the desired new name. Other fields are ignored
   */
  static async RenameDraftOfBillable(draft) {
    var verb = 'PUT'
    var path = this.SERVICE_ROUTE + '/' + this.BILLABLE_DRAFT_PATH
    var body = draft

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Removes a draft from the collection of calling user's drafts in the current billable.
   * In case the draft is already used for any shipment the draft will no longer
   * appear as an existing draft but the shipments remain along with their originated draft.
   * @param {string} draftId Internal unique identifier of the draft to be removed
   */
  static async RemoveDraft(draftId) {
    var verb = 'DELETE'
    var path = this.SERVICE_ROUTE + '/' + this.DRAFT_PATH + "/" + draftId
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Removes a draft from the collection of drafts in the current billable, of any user in billable.
   * In case the draft is already used for any shipment the draft will no longer
   * appear as an existing draft but the shipments remain along with their originated draft.
   * @param {string} draftId Internal unique identifier of the draft to be removed
   */
  static async RemoveDraftOfBillable(draftId) {
    var verb = 'DELETE'
    var path = this.SERVICE_ROUTE + '/' + this.BILLABLE_DRAFT_PATH + "/" + draftId
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides the list of shipments stored for the calling user in the current billable.
   * This is a paging request, the response may not contain all shipments.
   * For paging considerations, the collection of shipments of the calling user is provided sorted by save time descending.
   * @param {int} pageSize Paging argument: maximum amount of shipments to be provided in this call
   * @param {int} page Paging argument: the page to provide, 0-based
   * @returns {Shipment[]} The information about the shipments in the requested page
   */
  static async GetShipments(pageSize, page, includeemitted, includeprogrammed, includesaved, filter) {
    var verb = 'POST'
    var path = this.SERVICE_ROUTE + '/' + this.SHIPMENT_PATH + "?" +
      this.PAGE_SIZE_QUERY + "=" + pageSize + "&" +
      this.PAGE_QUERY + "=" + page + "&" +
      this.INCLUDE_EMITTED + "=" + includeemitted + "&" +
      this.INCLUDE_PROGRAMMED + "=" + includeprogrammed + "&" +
      this.INCLUDE_SAVED + "=" + includesaved
    var body = filter

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides the list of shipments stored in the current billable.
   * This is to be used by working group administrators which are allowed to access content from any user in the billable.
   * This is a paging request, the response may not contain all shipments.
   * For paging considerations, the collection of shipments of the calling user is provided sorted by save time descending.
   * @param {int} pageSize Paging argument: maximum amount of shipments to be provided in this call
   * @param {int} page Paging argument: the page to provide, 0-based
   * @returns {Shipment[]} The information about the shipments in the requested page
   */
  static async GetShipmentsOfBillable(pageSize, page, includeemitted, includeprogrammed, includesaved, filter) {
    var verb = 'POST'
    var path = this.SERVICE_ROUTE + '/' + this.BILLABLE_SHIPMENT_PATH + "?" +
      this.PAGE_SIZE_QUERY + "=" + pageSize + "&" +
      this.PAGE_QUERY + "=" + page + "&" +
      this.INCLUDE_EMITTED + "=" + includeemitted + "&" +
      this.INCLUDE_PROGRAMMED + "=" + includeprogrammed + "&" +
      this.INCLUDE_SAVED + "=" + includesaved
    var body = filter

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides shipment batches owned by the specified user in the current client scope.
   * @param {string} userId Identifier of the owner user
   * @param {string|null} searchText Optional search text for server-side filtering
   * @returns {ShipmentBatchInfo[]} Available batches for filtering
   */
  static async GetBatchesByUser(userId, searchText = null, pageSize = null, page = 0) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.BATCH_BY_USER_PATH + "?" + this.USER_ID_QUERY + "=" + encodeURIComponent(userId || "")
    if (searchText !== null && searchText !== undefined && searchText.toString().trim() !== "") {
      path += "&" + this.SEARCH_TEXT_QUERY + "=" + encodeURIComponent(searchText)
    }
    if (pageSize !== null && pageSize !== undefined) {
      path += "&" + this.PAGE_SIZE_QUERY + "=" + encodeURIComponent(pageSize)
      path += "&" + this.PAGE_QUERY + "=" + encodeURIComponent(page || 0)
    }
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
    * Provides shipment batches available in billable scope, optionally filtered by selected users/search text and paged.
     * @param {{ userIdFilter: string[], searchText?: string|null, pageSize?: number, page?: number }} filter Billable batch filter payload
   * @returns {ShipmentBatchInfo[]} Available batches for filtering
   */
  static async GetBatchesOfBillable(filter) {
    var verb = 'POST'
    var path = this.SERVICE_ROUTE + '/' + this.BATCH_OF_BILLABLE_PATH
    var body = filter || { userIdFilter: [] }

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides the information about a shipment given its human-handled code.
   * The requested shipment must be one owned by the calling user.
   * @param {string} shipmentCode Identifier of the shipment, like "PXXDMYXXM"
   * @returns {Shipment} The information about the shipment. Null if does not exist or not owner
   */
  static async GetShipmentByCode(shipmentCode) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.SHIPMENT_PATH + "/" + encodeURIComponent(shipmentCode)
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides the information about a shipment given its human-handled code.
   * The requested shipment must be one owned by a user of the billable the calling user is administrator.
   * @param {string} shipmentCode Identifier of the shipment, like "PXXDMYXXM"
   * @returns {Shipment} The information about the shipment. Null if does not exist or not owner
   */
  static async GetShipmentByCodeOfBillable(shipmentCode) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.BILLABLE_SHIPMENT_PATH + "/" + encodeURIComponent(shipmentCode)
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides the information about several shipments given their human-handled code.
   * This request is for system or factory users.
   * @param {string[]} shipmentCodes Array of shipment identifiers, like "PXXDMYXXM"
   * @returns {Shipment[]} The information about the shipments. Null if does not exist or not owner
   */
  static async GetShipmentsByCode(shipmentCodes) {
    var verb = 'POST'
    var path = this.SERVICE_ROUTE + '/' + this.SHIPMENT_LIST_PATH
    var body = shipmentCodes

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Creates or updates a shipment from existing draft documents for eventual emission and processing.
   * The shipment will be bound to the current session's user and billable.
   * @param {Shipment} shipment Detailed information about the shipment to create or update
   * @param {string} sendConfirmation (Optional) e-mail address where a confirmation will be sent. Must be one of the known addresses of the calling user. Message will be sent only for new shipments with emission time established
   * @param {string} cancellationReason (Optional) reason provided when cancellation is requested
   * @returns {Shipment} The same shipment structure but filled in with fields as generated by the system.
   *                     In case of shipment invalidated, a BadRequest with a JSON array of error messages:
   *                     [ERR_OUTBOX_INVALIDATED] { "Invalidation reason 1", "Invalidation reason 2"... }
   */
  static async StoreShipment(shipment, sendConfirmation, cancellationReason = null) {
    var verb = 'PUT'
    var path = this.SERVICE_ROUTE + '/' + this.SHIPMENT_PATH + "?" + this.SEND_CONFIRMATION_QUERY + "=" + encodeURIComponent(sendConfirmation)
    if (cancellationReason !== undefined && cancellationReason !== null && cancellationReason.toString().trim() !== '') {
      path += "&" + this.CANCELLATION_REASON_QUERY + "=" + encodeURIComponent(cancellationReason.toString().trim())
    }
    var body = shipment

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Creates or updates a shipment from existing draft documents for eventual emission and processing.
   * The shipment will be bound to the current session's user and billable.
   * @param {Shipment} shipments Detailed information about the shipment to create or update
   * @param {string} sendConfirmation (Optional) e-mail address where a confirmation will be sent. Must be one of the known addresses of the calling user. Message will be sent only for new shipments with emission time established
   * @returns {Shipment} The same shipment structure but filled in with fields as generated by the system.
   *                     In case of shipment invalidated, a BadRequest with a JSON array of error messages:
   *                     [ERR_OUTBOX_INVALIDATED] { "Invalidation reason 1", "Invalidation reason 2"... }
   */
  static async StoreShipments(shipments, sendConfirmation, sendEmailSummary, batchLabel) {
    var verb = 'PUT'
    var path = this.SERVICE_ROUTE + '/' + this.SHIPMENTS_BATCH_PATH + "?" + this.SEND_CONFIRMATION_QUERY + "=" + sendConfirmation + "&sendEmailSummary=" + sendEmailSummary
    if (batchLabel !== undefined && batchLabel !== null && batchLabel.toString().trim() !== "") {
      path += "&batchLabel=" + encodeURIComponent(batchLabel.toString().trim())
    }
    var body = shipments
    
    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }


  /**
 * Enqueues an existing shipment to be consolidated/processed again. The shipment is expected to be a previously processed one
 * which is desired to be sent again to destination because previous expedition failed to reach destination.
 * This action is for factory administrators only. This administrator determines the carrier product to use this time, may differ
 * from the carrier product assigned by product mapping. Thus, a shipment may have been initially processed as PK and now requested
 * to be processed as NT.
 * Note that it is allowed to redispatch a same shipment several times.
 * In case the shipment is already enqueued, this call will just replace the carrier product assigned to the shipment.
 * @param {string} shipmentIdOrCode Internal or external identifier of the shipment requested to be re-enqueued
 * @param {string} carrierProductCode Code of the carrier product to apply this time to the shipment. May differ from previous processing
 * @returns {Shipment} Error message or null on success
 */
  static async RedispatchShipment(shipmentIdOrCode, carrierProductCode) {
    var verb = 'PUT'
    var path = this.SERVICE_ROUTE + '/' + shipmentIdOrCode
    var body = carrierProductCode

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Removes or cancels a shipment owned by the calling user.
   * For not yet emitted shipments: the shipment is completely removed from the user's collection as if never stored.
   * For emitted but not processed shipments: the shipment is completely removed and the price refund to the user.
   * For processed shipments: the shipment is flagged as canceled.
   * @param {string} shipmentId Internal unique identifier of the shipment to remove
   * @param {boolean} removeDraft Whether to also remove the associated draft, if not used for other shipments and the shipment is not yet processed
   * @param {boolean} doNotCancel The removal only occurs if the shipment can be completely removed, so this call will do nothing if the shipment would need to be canceled instead of removed
   * @returns {boolean} Whether the shipment was removed, otherwise it was flagged as canceled
   */
  static async RemoveShipment(shipmentId, removeDraft) {
    var verb = 'DELETE'
    var path = this.SERVICE_ROUTE + '/' + this.SHIPMENT_PATH + "/" + shipmentId +
      "?" + this.REMOVE_DRAFT_QUERY + "=" + removeDraft
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Removes or cancels a shipment in the billable of the calling administrator.
   * For not yet emitted shipments: the shipment is completely removed from the user's collection as if never stored.
   * For emitted but not processed shipments: the shipment is completely removed and the price refund to the user.
   * For processed shipments: the shipment is flagged as canceled.
   * @param {string} shipmentId Internal unique identifier of the shipment to remove
   * @param {boolean} removeDraft Whether to also remove the associated draft, if not used for other shipments and the shipment is not yet processed
   * @param {boolean} doNotCancel The removal only occurs if the shipment can be completely removed, so this call will do nothing if the shipment would need to be canceled instead of removed
   * @returns {boolean} Whether the shipment was removed, otherwise it was flagged as canceled
   */
  static async RemoveShipmentOfBillable(shipmentId, removeDraft) {
    var verb = 'DELETE'
    var path = this.SERVICE_ROUTE + '/' + this.BILLABLE_SHIPMENT_PATH + "/" + shipmentId +
      "?" + this.REMOVE_DRAFT_QUERY + "=" + removeDraft
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }
  /**
     * Provides the base-64 image of one of the specified draft document's page.
     * Allows to specify a rectangle of the page to be provided instead of the whole page's image.
     * Hint: passing the following values will provide the whole page: left=0, top=0, width=100, height=100.
     * @param {boolean} thumbnail Whether to generate a thumbnail otherwise a high-resolution image
     * @param {string} shipmentId Internal unique identifier of the draft asking for
     * @param {int} pageNumber Number of the page in the draft document (starting from page 1)
     * @param {double} left Percentage of the whole image's width where the rectangle to provide will begin in its left edge
     * @param {double} top Percentage of the whole image's height where the rectangle to provide will begin in its top edge
     * @param {double} width PeGetShipmentPageImagercentage of the whole image's width the provided rectangle will have
     * @param {double} height Percentage of the whole image's height the provided rectangle will have
     * @returns {string} High resolution image of the page's content in base-64 format
     */
  static async GetShipmentPageImage(thumbnail, shipmentId, pageNumber, left, top, width, height) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.SHIPMENT_PAGE_IMAGE_PATH + '/' + shipmentId +
      "?" + this.PAGE_NUMBER_QUERY + "=" + pageNumber +
      "&" + this.LEFT_QUERY + "=" + left +
      "&" + this.TOP_QUERY + "=" + top +
      "&" + this.WIDTH_QUERY + "=" + width +
      "&" + this.HEIGHT_QUERY + "=" + height +
      "&" + this.THUMB_QUERY + "=" + thumbnail
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides the content of the shipment's document.
   * The calling user must be the owner of the shipment.
   * @param {string} shipmentIdOrCode Internal or external identifier of the shipment asking for its document
   * @returns The content of the PDF file for browser download
   */
  static async DownloadShipmentDocument(shipmentIdOrCode) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.SHIPMENT_PATH + '/' + shipmentIdOrCode + '/' + this.FILE_CONTENT_PATH
    var body = null

    var fileName = null
    return fetch(this.devPrefix + path, AuthCli.GetRequestOptions(verb, body, true))
      .then(response => {
        if (response.status === 200) {
          const header = response.headers.get('Content-Disposition');
          const parts = header ? header.split(';') : null;
          fileName = parts ? parts[1].split('=')[1] : null;
          return response.blob()
        }
        else if (response.status === 500) return response.text()
      })
      .then(blobby => {
        if (blobby == null) return
        if (blobby.toString().startsWith('[ERR')) return blobby.toString()

        // Force the received content to appear as a downloaded file.
        let anchor = document.createElement("a");
        document.body.appendChild(anchor);
        let objectUrl = window.URL.createObjectURL(blobby);
        anchor.href = objectUrl;
        anchor.download = fileName ? fileName : shipmentIdOrCode + ".pdf";
        anchor.click();
        window.URL.revokeObjectURL(objectUrl);
      });
  }

/**
 * Provides the content of the shipment's document.
 * The calling user must be billable admin of the shipment
 * @param {string} shipmentIdOrCode Internal or external identifier of the shipment asking for its document
 * @param {String} fileName File name with extension as expected to be stored in local file system.
 * @returns The content of the PDF file for browser download
 */
  static async DownloadShipmentDocumentOfBillable(shipmentIdOrCode, fileName) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.SHIPMENT_PATH + '/' + shipmentIdOrCode + '/' + this.BILLABLE_FILE_CONTENT_PATH
    var body = null

    return fetch(this.devPrefix + path, AuthCli.GetRequestOptions(verb, body, true))
      .then(response => {
        if (response.status === 200) {
          const header = response.headers.get('Content-Disposition');
          const parts = header ? header.split(';') : null;
          fileName = parts ? parts[1].split('=')[1] : null;
          return response.blob()
        }
        else if (response.status === 500) return response.text()
      })
      .then(blobby => {
        if (blobby == null) return
        if (blobby.toString().startsWith('[ERR')) return blobby.toString()

        // Force the received content to appear as a downloaded file.
        let anchor = document.createElement("a");
        document.body.appendChild(anchor);
        let objectUrl = window.URL.createObjectURL(blobby);
        anchor.href = objectUrl;
        anchor.download = fileName ? fileName : shipmentIdOrCode + ".pdf";
        anchor.click();
        window.URL.revokeObjectURL(objectUrl);
      });
  }

  /**
   * Applies an OCR algorithm looking for postal addresses present in one of the pages of a draft document.
   * @param {*} draftId Internal unique identifier of the draft asking for
   * @param {*} pageNumber Number of the page in the draft document (starting from page 1)
   * @param {*} left Percentage of the whole image's width where the rectangle to provide will begin in its left edge
   * @param {*} top Percentage of the whole image's height where the rectangle to provide will begin in its top edge
   * @param {*} width Percentage of the whole image's width the provided rectangle will have
   * @param {*} height Percentage of the whole image's height the provided rectangle will have
   * @returns Found address or addresses (the first is the main proposal, others are alternatives). The raw text lines detected are placed in envelopeBox array. Other fields are filled in from the raw text in case it was possible to identify their contents
   */
  static async DetectAddressesInPage(draftId, pageNumber, left, top, width, height) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.DETECTED_ADDRESSES + '/' + draftId +
      "?" + this.PAGE_NUMBER_QUERY + "=" + pageNumber +
      "&" + this.LEFT_QUERY + "=" + left +
      "&" + this.TOP_QUERY + "=" + top +
      "&" + this.WIDTH_QUERY + "=" + width +
      "&" + this.HEIGHT_QUERY + "=" + height
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Applies an OCR process to a rectangle of a draft's page. The text found by the OCR will be returned as the shipment's reference proposal.
   * @param {*} draftId Internal unique identifier of the draft asking for
   * @param {*} pageNumber Number of the page in the draft document (starting from page 1)
   * @param {*} left Percentage of the whole image's width where the rectangle to provide will begin in its left edge
   * @param {*} top Percentage of the whole image's height where the rectangle to provide will begin in its top edge
   * @param {*} widthPercentage of the whole image's width the considered rectangle will have (pass value 0 to let the service use the billable's default rectangle)
   * @param {*} height Percentage of the whole image's height the considered rectangle will have (pass value 0 to let the service use the billable's default rectangle)
   * @returns The text extracted, the shipment's reference proposal
   */
  static async DetectReferenceInPage(draftId, pageNumber, left, top, width, height) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.DETECTED_REFERENCES + '/' + draftId +
      "?" + this.PAGE_NUMBER_QUERY + "=" + pageNumber +
      "&" + this.LEFT_QUERY + "=" + left +
      "&" + this.TOP_QUERY + "=" + top +
      "&" + this.WIDTH_QUERY + "=" + width +
      "&" + this.HEIGHT_QUERY + "=" + height
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }


  static async DetectShipmentsInDrafts(partialResult, draftIds, left, top, width, height) {
    var verb = 'POST'
    var path = this.SERVICE_ROUTE + '/' + this.DETECTED_SHIPMENTS +
      "?" + this.DRAFT_IDS_QUERY + "=" + draftIds +
      "&" + this.LEFT_QUERY + "=" + left +
      "&" + this.TOP_QUERY + "=" + top +
      "&" + this.WIDTH_QUERY + "=" + width +
      "&" + this.HEIGHT_QUERY + "=" + height
    var body = partialResult

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }



  static async CountShipments(includeemitted, includeprogrammed, includesaved, filter) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.SHIPMENT_COUNT_PATH + "?" +
      this.INCLUDE_EMITTED + "=" + includeemitted + "&" +
      this.INCLUDE_PROGRAMMED + "=" + includeprogrammed + "&" +
      this.INCLUDE_SAVED + "=" + includesaved + "&" +
      this.FILTER_QUERY + "=" + JSON.stringify(filter)
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  static async CountShipmentsOfBillable(includeemitted, includeprogrammed, includesaved, filter) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.BILLABLE_SHIPMENT_COUNT_PATH + "?" +
      this.INCLUDE_EMITTED + "=" + includeemitted + "&" +
      this.INCLUDE_PROGRAMMED + "=" + includeprogrammed + "&" +
      this.INCLUDE_SAVED + "=" + includesaved + "&" +
      this.FILTER_QUERY + "=" + JSON.stringify(filter)
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  static async GetShipmentBillingTotalsOfBillable(includeemitted, includeprogrammed, includesaved, filter) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.BILLABLE_SHIPMENT_BILLING_TOTALS_PATH + "?" +
      this.INCLUDE_EMITTED + "=" + includeemitted + "&" +
      this.INCLUDE_PROGRAMMED + "=" + includeprogrammed + "&" +
      this.INCLUDE_SAVED + "=" + includesaved + "&" +
      this.FILTER_QUERY + "=" + JSON.stringify(filter)
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Looks for shipments following some conditions and belonging to any billables.
   * This is a method for backoffice users.
   * This is a paging request, the response may not contain all available results.
   * For paging considerations, the collection of shipments is not guarranted to be sorted by any field.
   * @param {int} pageSize Paging argument: maximum amount of shipments to be provided in this call
   * @param {int} page Paging argument: the page to provide, 0-based
   * @param {ShipmentFilter} filter Conditions for those shipments to be included. Pass null for no filter
   * @param {string[]} billableIdFilter Internal identifiers of billables the search is limited to. If not identifiers are provided a global search will be done. In REST request: comma-separated list of identifiers
   * @returns {Shipment[]} The information about the shipments in the requested page
   */
  static async SearchShipments(pageSize, page, filter, billableIdFilter) {
    var verb = 'POST'
    var path = this.SERVICE_ROUTE + '/' + this.SHIPMENT_SEARCH_PATH + "?" +
      this.PAGE_SIZE_QUERY + "=" + pageSize + "&" +
      this.PAGE_QUERY + "=" + page
    var body = {
      filter: filter,
      billableIdFilter: billableIdFilter != null ? billableIdFilter : []
    }

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

}


// WEBPACK FOOTER //
// ./src/sdk/OutboxCli.js